import { STATI_RICHIESTA_CANCELLAZIONE, type RichiestaCancellazione } from '@aulaviva/shared';
import { Alert, Badge, Button, DataTable, Dialog, EmptyState, NativeSelect, TextArea, useToast, type ColumnDef } from '@aulaviva/ui';
import { CircleCheck, CircleX, Clock, UserX } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useCancellazioniAdmin, useDecidiCancellazione } from '@/api/amministrazione';
import { formattaDataOra } from '@/lib/date';
import { admin, amministrazione, comuni } from '@/testi';

const t = amministrazione.cancellazioni;

export function SchedaCancellazioni() {
  const toast = useToast();
  const [stato, setStato] = useState<string>('in_attesa');
  const richieste = useCancellazioniAdmin(stato || undefined);
  const decidi = useDecidiCancellazione();
  const [dialogo, setDialogo] = useState<{ r: RichiestaCancellazione; azione: 'approva' | 'rifiuta' } | null>(null);
  const [nota, setNota] = useState('');
  const [errore, setErrore] = useState<string | null>(null);

  const colonne = useMemo<ColumnDef<RichiestaCancellazione, unknown>[]>(
    () => [
      { header: t.colonne.chi, cell: ({ row }) => <span>{row.original.cognome} {row.original.nome}<br /><span className="text-ink-muted">{row.original.email}</span></span> },
      { header: t.colonne.motivo, cell: ({ row }) => row.original.motivo || <span className="text-ink-muted">{t.senzaMotivo}</span> },
      { header: t.colonne.richiesta, cell: ({ row }) => formattaDataOra(row.original.richiestaAt) },
      {
        header: t.colonne.stato,
        cell: ({ row }) => {
          const s = row.original.stato;
          return (
            <Badge variant={s === 'approvata' ? 'success' : s === 'rifiutata' ? 'error' : 'warning'} icon={s === 'approvata' ? CircleCheck : s === 'rifiutata' ? CircleX : Clock}>
              {t.stati[s]}
            </Badge>
          );
        },
      },
      {
        header: t.colonne.decisione,
        cell: ({ row }) => {
          const r = row.original;
          if (r.stato === 'in_attesa') {
            return (
              <span className="inline-flex gap-1">
                <Button variant="discreet" onClick={() => { setDialogo({ r, azione: 'approva' }); setNota(''); setErrore(null); }}>{t.approva}</Button>
                <Button variant="discreet" onClick={() => { setDialogo({ r, azione: 'rifiuta' }); setNota(''); setErrore(null); }}>{t.rifiuta}</Button>
              </span>
            );
          }
          return (
            <span className="text-ink-muted">
              {r.decisaDa && r.decisaAt ? t.decisaDa(r.decisaDa, formattaDataOra(r.decisaAt)) : ''}
              {r.nota && <><br />{r.nota}</>}
            </span>
          );
        },
      },
    ],
    [],
  );

  async function conferma() {
    if (!dialogo) return;
    setErrore(null);
    if (nota.trim().length < 3) {
      setErrore(t.notaAiuto);
      return;
    }
    try {
      await decidi.mutateAsync({ id: dialogo.r.id, azione: dialogo.azione, nota: nota.trim() });
      setDialogo(null);
      toast.mostra({ variant: 'success', title: t.decisa });
    } catch (e) {
      setErrore(e instanceof Error ? e.message : comuni.erroreGenerico);
    }
  }

  const nome = dialogo ? `${dialogo.r.nome} ${dialogo.r.cognome}` : '';
  return (
    <>
      <p className="t-body text-ink-muted max-w-[720px]">{t.intro}</p>
      <div className="max-w-[260px]">
        <NativeSelect label={t.filtro} placeholder={admin.comuni.tutti} value={stato} onChange={(e) => setStato(e.target.value)} options={STATI_RICHIESTA_CANCELLAZIONE.map((s) => ({ value: s, label: t.stati[s] }))} />
      </div>
      {richieste.isError && <Alert variant="error" title={richieste.error.message} />}
      <DataTable
        caption={amministrazione.schede.cancellazioni}
        columns={colonne}
        data={richieste.data ?? []}
        getRowId={(r) => r.id}
        loading={richieste.isPending}
        testi={admin.comuni}
        empty={
          <EmptyState icon={UserX} title={t.nessunaTitolo}>
            {t.nessunaTesto}
          </EmptyState>
        }
      />
      <Dialog
        open={dialogo !== null}
        onOpenChange={(o) => !o && setDialogo(null)}
        title={dialogo ? (dialogo.azione === 'approva' ? t.approvaTitolo(nome) : t.rifiutaTitolo(nome)) : ''}
        description={dialogo?.azione === 'approva' ? t.approvaTesto : t.rifiutaTesto}
        actions={
          <>
            <Button onClick={() => setDialogo(null)}>{admin.comuni.annulla}</Button>
            <Button variant="primary" onClick={conferma} loading={decidi.isPending} loadingText={admin.comuni.inCorso}>
              {dialogo?.azione === 'approva' ? t.approvaConferma : t.rifiutaConferma}
            </Button>
          </>
        }
      >
        {errore && <Alert variant="error" title={errore} />}
        <TextArea label={t.nota} hint={t.notaAiuto} value={nota} onChange={(e) => setNota(e.target.value)} rows={3} maxLength={500} required />
      </Dialog>
    </>
  );
}
